import { unreadMessagesStore } from '@/Sparrow/Components/Chat/unreadMessagesStore';
import useChatInboxSharedState from '@/Sparrow/Hooks/useChatInboxSharedState';
import useMixpanel from '@/Sparrow/Hooks/useMixpanel';
import { isDefined } from '@vueuse/core';
import { useAxios } from '@vueuse/integrations/useAxios';
import Echo from 'laravel-echo';
import {
  computed,
  onBeforeMount,
  onBeforeUnmount,
  reactive,
  ref,
  watchEffect,
} from 'vue';

export const echo = new Echo({
  broadcaster: 'pusher',
  key: import.meta.env.MIX_PUSHER_APP_KEY,
  cluster: import.meta.env.MIX_PUSHER_APP_CLUSTER,
  forceTLS: true,
  namespace: 'App.Sparrow.Chat.Events',
});

const { track } = useMixpanel();

const { currentChatId, unreadChatIds } = useChatInboxSharedState();

const instances = {};

const createChatInbox = (userId) => {
  const {
    data,
    isFinished,
    isLoading,
    execute,
  } = useAxios(route('api.chat.index'), axios, {
    onSuccess: () => {
      track('Chat Inbox Loaded', {
        'User ID': userId,
        'Loading Timestamp': performance.now(),
      });
    },
    onError: () => {
      track('Chat Inbox Loading Failed', {
        'User ID': userId,
        'Loading Timestamp': performance.now(),
      });
    },
  });

  const subscribed = ref(false);
  const chats = ref([]);

  const hasChats = computed(() => Number(chats.value.length) > 0);

  /**
   * Updates an existing chat in the inbox with incoming message data.
   *
   * @param {string} chatId - The ID of the chat to update
   * @param {Object} message - The new message
   */
  function addMessageToChatInbox(chatId, message) {
    const chatIndex = chats.value.findIndex((chat) => chat.id === chatId);
    if (chatIndex === -1) return;

    // Update the existing chat
    const updatedChat = reactive({
      ...chats.value[chatIndex],
      last_message_at: message.created_at,
      last_message_id: message.id,
      last_message: {
        id: message.id,
        chat_id: chatId,
        user_id: message.user_id,
        body: message.body,
        created_at: message.created_at,
        updated_at: message.updated_at,
        user: {
          id: message.user_id,
        },
      },
    });

    const tempChats = chats.value;
    tempChats.splice(chatIndex, 1, updatedChat);

    chats.value = tempChats.sort((a, b) => {
      if (a.last_message_at < b.last_message_at) {
        return 1;
      }
      if (a.last_message_at > b.last_message_at) {
        return -1;
      }
      return 0;
    });
  }

  async function subscribeToUserInbox() {
    if (subscribed.value) return;
    try {
      const channel = await echo.join(`App.Sparrow.Chat.User.${userId}`);
      channel.listen('MessageReceived', async (e) => {
        track('Message Received', {
          'User ID': userId,
          'Chat ID': e.chatId,
          'Message ID': e.message.id,
        });

        if (
          (isDefined(currentChatId) && e.chatId !== currentChatId.value && !unreadChatIds.value.includes(e.chatId))
          || !isDefined(currentChatId)
        ) {
          unreadChatIds.value = unreadChatIds.value.concat(e.chatId);
        }

        addMessageToChatInbox(e.chatId, e.message);
      });
    } catch (error) {
      console.error(error);
    }

    subscribed.value = true;
  }

  function unsubscribeFromUserInbox() {
    echo.leave(`App.Sparrow.Chat.User.${userId}`);
    subscribed.value = false;
  }

  onBeforeMount(() => {
    track('Chat Inbox Loading', {
      'User ID': userId,
      'Loading Timestamp': performance.now(),
    });
    execute();
  });

  watchEffect(() => {
    if ((isDefined(currentChatId) && unreadChatIds.value.includes(currentChatId.value))
      || !isDefined(currentChatId)) {
      unreadChatIds.value = unreadChatIds.value.filter((id) => id !== currentChatId.value);
    }
  });

  watchEffect(() => {
    unreadMessagesStore.setCount(unreadChatIds.value.length);
  });

  watchEffect(() => {
    if (isFinished && isDefined(data?.value?.chats?.data)) {
      chats.value = [
        ...chats.value,
        ...data.value.chats.data,
      ];
      unreadChatIds.value = data.value.unreadChatIds;
    }
  });

  onBeforeUnmount(() => {
    if (subscribed.value) {
      unsubscribeFromUserInbox();
    }
  });

  return {
    chats,
    currentChatId,
    hasChats,
    isFinished,
    isLoading,
    unreadChatIds,
    subscribeToUserInbox,
    unsubscribeFromUserInbox,
    addMessageToChatInbox,
  };
};

const useChatInbox = (userId) => {
  // If no userId is provided, return an empty state
  if (!userId) {
    return {
      chats: ref([]),
      currentChatId: ref(null),
      hasChats: computed(() => false),
      isFinished: ref(true),
      isLoading: ref(false),
      unreadChatIds: ref([]),
      subscribeToUserInbox: () => {},
      unsubscribeFromUserInbox: () => {},
      addMessageToChatInbox: () => {},
      refresh: () => {},
    };
  }

  // Create a new instance if one doesn't exist for this user
  if (!instances[userId]) {
    instances[userId] = createChatInbox(userId);
  }

  return instances[userId];
};

// Create a global reset function to clear instances when users change
useChatInbox.resetInstances = () => {
  // Reset the shared state
  currentChatId.value = null;
  unreadChatIds.value = [];

  // Clean up subscriptions for all instances
  Object.keys(instances).forEach((userId) => {
    try {
      instances[userId].unsubscribeFromUserInbox();
    } catch (e) {
      console.error(`Failed to unsubscribe user ${userId}:`, e);
    }
  });

  // Clear the instances
  Object.keys(instances).forEach((key) => {
    delete instances[key];
  });
};

export default useChatInbox;
